import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Button,
  Text,
  Checkbox,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
} from '@fluentui/react-components';
import { Play20Regular, Eye20Regular, Dismiss24Regular } from '@fluentui/react-icons';
import { useAppDispatch, useAppSelector } from '../hooks';
import {
  fetchLabelDeploymentCoverage,
  clearLabels,
  selectDeployableLabels,
  selectLabelsLoading,
  selectLabelsError,
  selectLabelsPagination,
  setDeploymentInFlight,
  clearOverridesForEntities,
  LabelUIStatus,
} from '../slices/labelDeploymentSlice';
import { batchDeploy } from '../services/deployment.service';
import {
  trackDeployments,
  selectActiveDeploymentCount,
} from '../slices/deploymentTrackingSlice';
import { selectTenantPrefix } from '../slices/tenantsSlice';
import type { LabelEntity } from '../services/types';
import type { BffDeploymentRequest, TrackedDeployment } from '../services/types';

import { StatusBadge, StatusType } from './StatusBadge';
import { FormattedDateTime } from './common/DateTimeWithUTCInfo';
import {
  PolicyTable,
  EntityFilterBar,
  PoliciesPaginationFooter,
  TENANT_GRID_COLUMNS,
} from './policies/shared';
import { STATUS_OPTIONS } from '../services/types';
import { ensureDomainSuffix } from './common/utils';
import { TenantPolicyTableStyles } from './Styles';
import LabelViewDetailsDialog from './labels/LabelViewDetailsDialog';
import LabelDeployConfirmDialog from './labels/LabelDeployConfirmDialog';
import TenantPrefixBox from './common/TenantPrefixBox';
import DeploymentWarningBanner from './common/DeploymentWarningBanner';

/** Map label UI status → StatusBadge StatusType */
function getLabelStatus(uiStatus: LabelUIStatus): StatusType {
  if (uiStatus === 'Deployed') return 'Available';
  if (uiStatus === 'InProgress') return 'In Progress';
  if (uiStatus === 'DeploymentFailed') return 'Failed';
  return 'Not Available';
}

// 48px checkbox | 2.5fr name | 0.8fr priority | 1.2fr status | 1.5fr last deployed | 1fr actions
const LABEL_TENANT_COLUMNS = TENANT_GRID_COLUMNS;

type Props = {
  tenantId: string;
  tenantDisplay?: string;
};

export default function TenantLabelsTable({ tenantId, tenantDisplay }: Props) {
  const styles = TenantPolicyTableStyles();
  const dispatch = useAppDispatch();

  const labels = useAppSelector(selectDeployableLabels);
  const loading = useAppSelector(selectLabelsLoading);
  const error = useAppSelector(selectLabelsError);
  const pagination = useAppSelector(selectLabelsPagination);
  const activeDeployCount = useAppSelector(selectActiveDeploymentCount);
  const baseTenantName = useAppSelector(s => s.labelDeployment.baseTenantName);
  const uiStatusOverrides = useAppSelector(s => s.labelDeployment.uiStatusOverrides);
  const prefix = useAppSelector(s => selectTenantPrefix(s, tenantId));

  // tenantDisplay is in "Name — UUID" format; extract just the short name for dialog titles
  const resolvedTargetName = useMemo(() => {
    if (tenantDisplay) {
      const namePart = tenantDisplay.split(' — ')[0].trim();
      if (namePart && namePart !== 'Tenant') return ensureDomainSuffix(namePart);
    }
    return ensureDomainSuffix(tenantId);
  }, [tenantDisplay, tenantId]);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [pageNumber, setPageNumber] = useState(1);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [detailsLabel, setDetailsLabel] = useState<LabelEntity | null>(null);
  const [deploying, setDeploying] = useState(false);
  const [deployConfirmOpen, setDeployConfirmOpen] = useState(false);
  const [deployMessage, setDeployMessage] = useState<{
    intent: 'success' | 'error' | 'warning' | 'info';
    title: string;
    body: string;
  } | null>(null);

  useEffect(() => {
    setPageNumber(1);
    setSelectedIds(new Set());
    dispatch(clearLabels());
  }, [tenantId, dispatch]);

  // Server-side label search is not implemented in the BFF, so when a search term is
  // active we fetch a single large page and filter client-side, surfacing matches from
  // across all pages together (not just the current page). The 5000 cap is a safety
  // ceiling far above the realistic sensitivity-label count per tenant; matches beyond
  // it would not be fetched, so revisit if a tenant ever approaches that many labels.
  const isSearching = search.trim().length > 0;
  useEffect(() => {
    if (!tenantId) return;
    dispatch(
      fetchLabelDeploymentCoverage({
        targetTenantIds: [tenantId],
        searchTerm: search || undefined,
        pageNumber: isSearching ? 1 : pageNumber,
        pageSize: isSearching ? 5000 : 25,
      }),
    );
  }, [tenantId, search, pageNumber, dispatch, isSearching]);

  const getTenantStatus = useCallback(
    (label: LabelEntity): LabelUIStatus => {
      const key = `${label.entityId.toLowerCase()}::${tenantId.toLowerCase()}`;
      const override = uiStatusOverrides[key];
      if (override) return override;
      const td = label.tenantDeployments.find(
        t => t.tenantId.toLowerCase() === tenantId.toLowerCase(),
      );
      if (!td) return 'NotDeployed';
      return td.deploymentStatus === 'Deployed' ? 'Deployed' : 'NotDeployed';
    },
    [tenantId, uiStatusOverrides],
  );

  const filtered = useMemo(() => {
    const tid = tenantId.toLowerCase();
    let result = labels;
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(l => l.data.displayName.toLowerCase().includes(q));
    }
    if (statusFilter !== 'All') {
      result = result.filter(l => {
        const td = l.tenantDeployments.find(t => t.tenantId.toLowerCase() === tid);
        return (td?.deploymentStatus === 'Deployed' ? 'Deployed' : 'NotDeployed') === statusFilter;
      });
    }
    return result;
  }, [labels, search, statusFilter, tenantId]);

  const selectableIds = useMemo(
    () => new Set(filtered.filter(l => getTenantStatus(l) !== 'Deployed').map(l => l.entityId)),
    [filtered, getTenantStatus],
  );

  const allSelected = selectableIds.size > 0 && [...selectableIds].every(id => selectedIds.has(id));
  const someSelected = [...selectableIds].some(id => selectedIds.has(id)) && !allSelected;

  const handleToggleSelect = (entityId: string, checked: boolean) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (checked) next.add(entityId);
      else next.delete(entityId);
      return next;
    });
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) setSelectedIds(new Set([...selectableIds]));
    else setSelectedIds(new Set());
  };

  const handleDeployConfirmed = useCallback(async () => {
    if (selectedIds.size === 0) return;
    setDeployConfirmOpen(false);
    const toDeployLabels = labels.filter(l => selectedIds.has(l.entityId));
    setDeploying(true);
    // Optimistic update: show InProgress immediately (same pattern as policies)
    toDeployLabels.forEach(l => {
      dispatch(setDeploymentInFlight({ entityId: l.entityId, tenantIds: [tenantId] }));
    });
    try {
      const req: BffDeploymentRequest = {
        entityType: 'LABEL',
        entities: toDeployLabels.map(l => ({
          id: l.data.name,
          targets: [{ tenantId }],
        })),
      };
      const result = await batchDeploy(req);

      const now = Date.now();
      // BFF returns entityId = label name (the id we sent); map back to the
      // local GUID so polling and uiStatusOverrides use the same key.
      const tracked: TrackedDeployment[] = result.acceptedItems.map(a => {
        const matched = toDeployLabels.find(
          l => l.data.name.toLowerCase() === (a.entityId ?? '').toLowerCase(),
        );
        return {
          deploymentId: a.deploymentId,
          tenantId: a.tenantId,
          entityId: matched?.entityId ?? a.entityId,
          policyName: a.entityId,
          entityType: 'LABEL',
          view: 'tenant',
          apiStatus: 'Pending',
          startedAt: now,
          lastPolledAt: now,
        };
      });
      if (tracked.length > 0) dispatch(trackDeployments(tracked));

      setSelectedIds(new Set());

      if (result.success && result.failedCount === 0 && result.conflictCount === 0) {
        setDeployMessage({
          intent: 'success',
          title: 'Deployment requested',
          body: prefix
            ? `${toDeployLabels.length} label(s) are being deployed. Parent and standalone labels will be prefixed with "${prefix}"; the prefixed display name may take 5–10 minutes to sync, after which a page refresh will show it.`
            : `${toDeployLabels.length} label(s) are being deployed.`,
        });
      } else if (!result.success) {
        setDeployMessage({ intent: 'error', title: 'Deployment rejected', body: result.message });
      } else {
        setDeployMessage({ intent: 'warning', title: 'Partial deployment', body: result.message });
      }
    } catch (e) {
      // Roll back the optimistic InProgress overrides
      dispatch(clearOverridesForEntities(
        toDeployLabels.map(l => ({ entityId: l.entityId, tenantIds: [tenantId] })),
      ));
      setDeployMessage({
        intent: 'error',
        title: 'Deployment failed',
        body: e instanceof Error ? e.message : 'Unknown error',
      });
    } finally {
      setDeploying(false);
    }
  }, [selectedIds, labels, tenantId, dispatch, prefix]);

  const selectedLabels = useMemo(
    () => labels.filter(l => selectedIds.has(l.entityId)),
    [labels, selectedIds],
  );

  const title = tenantDisplay ? `Sensitivity labels for ${tenantDisplay}` : 'Sensitivity labels';

  return (
    <div
      style={{
        background: 'white',
        border: '1px solid #e5e5e5',
        borderRadius: 8,
        padding: '0 24px 24px 24px',
      }}
    >
      {/* Header — mirrors PolicyTableContainer */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '24px 0 20px 0',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>{title}</h2>
          <Text size={300} style={{ color: '#6b7280' }}>
            Manage sensitivity labels for this tenant
          </Text>
          <div style={{ marginTop: 8 }}>
            <TenantPrefixBox prefix={prefix} />
          </div>
        </div>
        <div />
      </div>

      <div style={{ height: 1, background: '#e5e5e5', margin: '0 -24px 24px -24px' }} />

      {/* Error */}
      {error && (
        <div style={{ marginBottom: 16 }}>
          <MessageBar intent="error">
            <MessageBarBody>
              <MessageBarTitle>Error loading labels</MessageBarTitle>
              {error}
            </MessageBarBody>
          </MessageBar>
        </div>
      )}

      {/* Deployment result feedback */}
      {deployMessage && (
        <MessageBar intent={deployMessage.intent} style={{ marginBottom: 12 }}>
          <MessageBarBody>
            <MessageBarTitle>{deployMessage.title}</MessageBarTitle>
            {deployMessage.body}
          </MessageBarBody>
          <Button
            appearance="subtle"
            size="small"
            icon={<Dismiss24Regular />}
            onClick={() => setDeployMessage(null)}
            aria-label="dismiss message"
          />
        </MessageBar>
      )}

      {/* Prefix redeploy warnings — non-blocking, dismissible (shared across views) */}
      <DeploymentWarningBanner tenantId={tenantId} />

      {/* Active deployment polling indicator */}
      {activeDeployCount > 0 && (
        <MessageBar intent="info" style={{ marginBottom: 12 }}>
          <MessageBarBody>
            <MessageBarTitle>Deployment In Progress</MessageBarTitle>
            {activeDeployCount} deployment{activeDeployCount > 1 ? 's' : ''} in progress. Status updates automatically every 15 seconds.
          </MessageBarBody>
        </MessageBar>
      )}

      {/* Filter bar — search + batch deploy button */}
      <EntityFilterBar
        search={search}
        onSearchChange={(v) => { setSearch(v); setPageNumber(1); }}
        filterOptions={STATUS_OPTIONS.filter(o => o.key === 'All' || o.key === 'Deployed' || o.key === 'NotDeployed').map(o => o.key === 'All' ? { ...o, label: 'All' } : o) as unknown as { key: string; label: string }[]}
        filterValue={statusFilter}
        onFilterChange={(v) => { setStatusFilter(v); }}
        filterAriaLabel="Filter by status"
        showFilter={true}
        searchAriaLabel="Search labels"
        searchPlaceholder="Search labels..."
        className={styles.solidField}
      >
        <div style={{ flex: 1 }} />
        <Button
          appearance="primary"
          icon={<Play20Regular />}
          disabled={selectedIds.size === 0 || deploying}
          onClick={() => setDeployConfirmOpen(true)}
        >
          Deploy ({selectedIds.size})
        </Button>
      </EntityFilterBar>

      {/* Table — same PolicyTable wrapper as policies */}
      <PolicyTable
        columns={[
          { key: 'select', label: '', width: '48px' },
          { key: 'name', label: 'Label Name', width: 'minmax(250px,2.5fr)' },
          { key: 'priority', label: 'Priority', width: 'minmax(100px,0.8fr)' },
          { key: 'status', label: 'Status', width: 'minmax(140px,1.2fr)' },
          { key: 'lastModified', label: 'Last Modified Time', width: 'minmax(180px,1.5fr)', isTimeColumn: true },
          { key: 'actions', label: 'Actions', width: 'minmax(120px,1fr)', align: 'center' },
        ]}
        gridTemplateColumns={LABEL_TENANT_COLUMNS}
        loading={loading}
        loadingMessage="Loading labels..."
        isEmpty={filtered.length === 0}
        emptyTitle="No labels found"
        emptyMessage="No sensitivity labels match your search"
        selectAllState={{
          checked: allSelected ? true : someSelected ? 'mixed' : false,
          onChange: handleSelectAll,
        }}
      >
        {filtered.map((label, i) => {
          const uiStatus = getTenantStatus(label);
          const td = label.tenantDeployments.find(
            t => t.tenantId.toLowerCase() === tenantId.toLowerCase(),
          );
          const isLastRow = i === filtered.length - 1;
          const isSelected = selectedIds.has(label.entityId);
          return (
            <div
              key={label.entityId}
              style={{
                display: 'grid',
                gridTemplateColumns: LABEL_TENANT_COLUMNS,
                alignItems: 'center',
                padding: '16px 20px',
                fontSize: 14,
                borderBottom: isLastRow ? 'none' : '1px solid #f0f0f0',
                background: isSelected ? '#f0f7ff' : 'white',
              }}
            >
              <div>
                <Checkbox
                  checked={isSelected}
                  disabled={uiStatus === 'Deployed'}
                  onChange={(_, data) => handleToggleSelect(label.entityId, !!data.checked)}
                  aria-label={`Select label ${label.data.displayName}`}
                  input={{ 'aria-label': `Select label ${label.data.displayName}` }}
                />
              </div>
              <div style={{ fontWeight: 600 }}>{label.data.displayName}</div>
              <div style={{ color: '#374151' }}>{label.data.priority}</div>
              <div>
                <StatusBadge status={getLabelStatus(uiStatus)} />
              </div>
              <div>
                {label.data.metadata.lastModifiedDateTime ? (
                  <FormattedDateTime dateTime={label.data.metadata.lastModifiedDateTime} />
                ) : (
                  <span style={{ color: '#6b7280' }}>—</span>
                )}
              </div>
              <div style={{ display: 'flex', justifyContent: 'center', gap: 8 }}>
                <Button
                  appearance="secondary"
                  size="small"
                  icon={<Eye20Regular />}
                  onClick={() => setDetailsLabel(label)}
                >
                  View Details
                </Button>
              </div>
            </div>
          );
        })}
      </PolicyTable>

      {/* Pagination — hidden while searching (all matches shown on one page) */}
      {!isSearching && (
        <PoliciesPaginationFooter
          pageNumber={pagination.pageNumber}
          totalPages={pagination.totalPages}
          pageSize={pagination.pageSize}
          hasNextPage={pagination.hasNextPage}
          loading={loading}
          onPageChange={setPageNumber}
        />
      )}

      {/* View Details Dialog */}
      <LabelViewDetailsDialog
        open={!!detailsLabel}
        onClose={() => setDetailsLabel(null)}
        label={detailsLabel}
        baseTenantName={ensureDomainSuffix(baseTenantName)}
        targetTenantName={resolvedTargetName}
        tenantId={tenantId}
        prefix={prefix}
      />

      {/* Deploy Confirmation Dialog */}
      <LabelDeployConfirmDialog
        open={deployConfirmOpen}
        labels={selectedLabels}
        tenantId={tenantId}
        tenantDisplay={resolvedTargetName}
        baseTenantName={ensureDomainSuffix(baseTenantName)}
        prefix={prefix}
        deploying={deploying}
        onConfirm={handleDeployConfirmed}
        onClose={() => setDeployConfirmOpen(false)}
      />
    </div>
  );
}
